import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import {
  ITopOrderProduct,
  IVariantView,
} from "../../../Api/Interface/api.interface";
import {
  useGetAllPurDhashRecentTopProductQuery,
  useGetProductWithStoreQuery,
} from "../../../Api/attoDeskApi";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Pagination from "../../../Components/Pagination";
import { Link } from "react-router-dom";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { IoIosImages } from "react-icons/io";
import { SizeOfLevelType } from "../../../Core/Enum/enum";
import ReplaceImage from "../../../Images/repImg.png";

AnnotationsModule(Highcharts);

const convertUnit = (unit: number) => {
  switch (unit) {
    case SizeOfLevelType.kg:
      return "kg";
    case SizeOfLevelType.small:
      return "small";
    case SizeOfLevelType.li:
      return "li";
    case SizeOfLevelType.Medium:
      return "Medium";
    case SizeOfLevelType.Bottle:
      return "Bottle";
    case SizeOfLevelType.Each:
      return "Each";
    case SizeOfLevelType.Large:
      return "Large";
    case SizeOfLevelType.case:
      return "Case";
    case SizeOfLevelType.lb:
      return "lb";
    default:
      return "";
  }
};

const AttendanceTimeline = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const {
    data: SentToManagerData,
    isLoading: SentToManagerIsLoading,
    isError: SentToManagerIsError,
  } = useGetAllPurDhashRecentTopProductQuery({
    searchText: "",
    orderListStatus: "",
    id: "",
  });

  const { data: productCountData, isLoading: productCountIsLoading } =
    useGetProductWithStoreQuery({
      searchText: "",
      id: null,
      variantBrandId: null,
      categoryId: null,
      variantTagId: null,
      productViewOnline: null,
      createdDateStart: null,
      createdDateEnd: null,
      createdDate: null,
      mainItemId: null,
    });

  const SentToManagerList: ITopOrderProduct[] = Array.isArray(
    SentToManagerData?.data
  )
    ? (SentToManagerData?.data as ITopOrderProduct[])
    : [];

  const productList = useMemo(() => {
    return productCountData?.data as IVariantView[];
  }, [productCountData?.data]);

  const variantMap = useMemo(() => {
    const map = new Map<
      string,
      {
        name: string;
        displayname: string;
        variantImg: string;
        variantRefNumber: string;
        variantUnit: number;
      }
    >();

    productList?.forEach((teamMember) => {
      map.set(teamMember.id, {
        name: teamMember.variantName,
        variantRefNumber: teamMember.variantRefNumber,
        variantImg: teamMember.variantImg,
        variantUnit: teamMember.variantUnit,
        displayname: teamMember.variantDisplayName,
      });
    });

    return map;
  }, [productList]);

  return (
    <div
      style={{
        padding: "5px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", background: "#fafafa" }}>
                  <strong>#</strong>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    background: "#fafafa",
                    minWidth: "300px",
                  }}
                >
                  <strong>Items</strong>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", background: "#fafafa" }}
                >
                  <strong>No Order</strong>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    background: "#fafafa",
                    minWidth: "100px",
                  }}
                >
                  <strong></strong>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    background: "#fafafa",
                    minWidth: "130px",
                  }}
                >
                  <strong>Total Qty</strong>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    background: "#fafafa",
                    minWidth: "150px",
                  }}
                >
                  <strong>Total Price</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SentToManagerIsLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10vh"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : SentToManagerIsError || SentToManagerList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {SentToManagerIsError
                      ? "Error fetching data"
                      : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                SentToManagerList.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                ).map((row: ITopOrderProduct, index) => {
                  const variantUnit = variantMap.get(
                    row.productId
                  )?.variantUnit;
                  const totalOrders = SentToManagerData?.ordersCount ?? 0;
                  const percentage =
                    totalOrders > 0
                      ? Math.round((row.total_orders / totalOrders) * 100)
                      : 0;

                  return (
                    <TableRow key={index} hover>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>
                        <Link
                          to={`/variant/view/${row.productId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs="auto">
                              <Box
                                className={
                                  classes.SelectImageTableBoxwithBorder
                                }
                              >
                                {(row?.productId &&
                                  variantMap.get(row?.productId)?.variantImg) ||
                                ReplaceImage ? (
                                  <img
                                    src={
                                      variantMap.get(row.productId)
                                        ?.variantImg || ReplaceImage
                                    }
                                    alt="img"
                                    className={classes.SelectImageTable}
                                    onError={(e) =>
                                      (e.currentTarget.src = ReplaceImage)
                                    }
                                  />
                                ) : (
                                  <IoIosImages
                                    className={classes.SelectImageTable}
                                  />
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs>
                              <Typography className={classes.nameTableList}>
                                {toProperCase(
                                  variantMap.get(row?.productId)?.name ||
                                    variantMap.get(row?.productId)
                                      ?.displayname ||
                                    ""
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Link>
                      </TableCell>

                      <TableCell align="center">{row.total_orders}</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{ position: "relative", display: "inline-flex" }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={percentage}
                            size={42}
                            thickness={5}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: "absolute",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{ fontWeight: 600 }}
                            >
                              {percentage}%
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {row.total_quantity ?? 0}
                        {variantUnit !== undefined &&
                          ` ${convertUnit(variantUnit)}`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {`$ ${Number(row.total_revenue ?? 0).toFixed(2)}`}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};

export default AttendanceTimeline;
